/*
 * @Author: junjie.lean
 * @Date: 2019-08-06 14:21:02
 * @Last Modified by: xiangr
 * @Last Modified time: 2021-12-14 10:20:48
 * @description axios 单后台请求方式封装
 */

import Vue from 'vue'
import axios from 'axios'
import { readLocal, readSion, saveSion } from '@/view/public/common'
import md5Encrypt from '@/utils/md5Encrypt'
import { version } from '../../package.json'

const isDev = process.env.NODE_ENV === 'development' // 开发环境
const timeoutInterfaceList = ['/api/infoInput/export'] // 上传文件和下载文件接口

/**
 * 数据请求相关配置
 */
let requestConfig = {
    dataService: '', // 数据服务入口
    version: version, // 版本号
    alias: 'info_input', // 系统名称
    token: ''
  },
  axiosIns

/**
 * @description 设置数据服务配置
 * @param {string} dataService 数据服务地址
 * @param {string} token token
 * @param {string} imToken imToken
 */
export const setConfig = (dataService, token, imToken, planCode) => {
  dataService = dataService || '/'
  if (dataService.indexOf('/') < 0) {
    dataService = dataService + '/'
  }
  requestConfig.dataService = dataService
  axios.defaults.timeout = 1000 * 10 // 10秒超时

  if (token || readSion('token')) {
    axios.defaults.headers.common['Token'] = readSion('token')
  } else {
    //为空时不传
    delete axios.defaults.headers.common['Token']
  }

  if (planCode || readSion('planCode')) {
    axios.defaults.headers.common['examPlanCode'] = readSion('planCode')
  }

  axios.defaults.withCredentials = true //让ajax携带cookie
  axiosIns = axios.create({
    baseURL: dataService
  })
  requestConfig.token = token
}

/**
 * 请求数据服务
 * @param {string} method 请求的方法
 * @param {object} params 提交参数
 * @param {function} success 成功回调
 * @param {function} fail 失败回调
 * @param {boolean} isBlob 是否返回二进制流
 */
export const request = (
  method = '',
  params = {},
  success = data => {},
  fail = err => {},
  isBlob = false
) => {
  let opts = requestConfig
  if (!opts.dataService) {
    throw new Error('需要设置数据服务地址，请执行setConfig进行设置')
  }
  if (method) {
    // 如果是上传文件和下载文件接口，超时时间设置为60分钟
    if (timeoutInterfaceList.includes(method)) {
      axiosIns.defaults.timeout = 1000 * 60 * 60
    }

    // 请求参数
    let postData = {
      version: opts.version,
      data: handleParams(params) || {},
      alias: opts.alias
    }

    // 使用md5加密生成加密字符串，供网关安全验证
    let str = JSON.stringify(postData) + readSion('token')
    let newMd5Str = md5Encrypt(str, 32)

    // 基础配置
    let config = {
      responseType: params.responseType,
      headers: {
        'client-type': readLocal('client') === 'APP' ? 'app' : 'web',
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': readSion('token'),
        'X-XSRF-TOKEN-COOKIE': readSion('token'),
        'Overstep-Control': newMd5Str,
        ...params.headers
      }
    }

    // 是否返回二进制流
    if (isBlob) {
      config.responseType = 'blob'
    }

    const ajaxObj = axiosIns.post(method, `${JSON.stringify(postData)}`, config)
    ajaxObj
      .then(response => {
        // 是否为专网
        const isPrivateNetwork = isDev ? true : response.headers['isvpn'] === 'true'
        saveSion('networkType', isPrivateNetwork)

        if (typeof success === 'function') {
          if (isBlob) {
            success(response)
          } else {
            success(response.data)
          }
        }
      })
      .catch(err => {
        // 错误回调
        if (typeof fail === 'function') {
          fail(err.response)
        }

        // 401-Token过期
        if (err.response && err.response.status) {
          err.response.status === 401 && noToken()
        }

        // TODO: 网络连接超时
        if (err.code === 'ECONNABORTED' && err.message.indexOf('timeout') !== -1) {
          Vue.prototype.$message.warn('网络连接超时')
        }

        // TODO: 网络异常，请检查网络
        if (err.message === 'Network Error') {
          Vue.prototype.$message.warn('网络异常，请检查网络')
        }

        // TODO: 无权限通知门户跳转到403页面
        if (err.response && err.response.status === 403) {
          noPermission()
        }
      })

    return ajaxObj
  } else {
    throw new Error('缺失参数‘Method’')
  }
}

// 处理请求参数data
function handleParams(params) {
  // 判断参数是否为对象
  if (Object.prototype.toString.call(params) === '[object Object]') {
    const urlParams = readSion('urlParams') || {}
    const resultPlanId = urlParams['planId'] || params['planId']

    return {
      ...params,
      planId: resultPlanId
    }
  }

  return params
}

/**
 * @description 同时进行多个请求，全部完成时执行回调
 * @param {Array} requestList 请求列表[{method:'',params:{}}...]
 * @param success
 * @param fail
 */
export const requestMultiple = (requestList, success, fail) => {
  let reqlist, axiosobj
  requestList = requestList || []

  if (requestList.length === 0) {
    throw new Error('arguments 不能为空')
  }

  reqlist = requestList.map(item => {
    return request(item.method, item.params, item.success, item.fail)
  })

  axiosobj = axios.all(reqlist)

  if (typeof success === 'function') {
    axiosobj.then(
      axios.spread(function () {
        let rets = []
        for (let i = 0; i < arguments.length; i++) {
          rets.push(arguments[i])
        }
        success(...rets)
      })
    )
  }

  if (typeof fail === 'function') {
    axiosobj.catch(fail)
  }
}

// Token过期处理
function noToken() {
  const clientType = readLocal('client')
  const params = {
    type: 'noToken',
    name: 'token过期',
    data: 'no token no systemToken',
    code: 401
  }
  window.top && window.top.postMessage(params, '*')
  window.parent && window.parent.postMessage(params, '*')

  if (clientType === 'APP') {
    // token失效，通知Android重新加载页面
    window.setupWebViewJavascriptBridge(bridge => {
      bridge.callHandler('tokenFailure')
    })
  } else {
    window.open('/#/web/noauth', '_self')
  }
}

// 403无权限处理
function noPermission() {
  const params = {
    code: 403,
    type: '403',
    name: 'noPermission',
    data: '无权限'
  }

  window.top && window.top.postMessage(params, '*')
  window.parent && window.parent.postMessage(params, '*')
}



// WEBPACK FOOTER //
// ./src/utils/request.js